// --- Configuration ---
const DAY_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

// --- Utility: Get Closure Dates ---
function getClosureSet(closures) {
  const closureDates = new Set();
  for (const c of (closures || [])) {
    if (c.type === 'range' && c.date_end) {
      const cur2 = new Date(c.date + 'T00:00:00');
      const end2 = new Date(c.date_end + 'T00:00:00');
      while (cur2 <= end2) {
        closureDates.add(cur2.toLocaleDateString('en-CA'));
        cur2.setDate(cur2.getDate() + 1);
      }
    } else {
      closureDates.add(c.date);
    }
  }
  return closureDates;
}

/**
 * Calcul précis des heures ouvrées entre deux dates
 */
export function calcBusinessHours(startDate, endDate, businessHours, closures) {
  if (!startDate || !endDate || !businessHours) return null;
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (isNaN(start) || isNaN(end) || end <= start) return 0;

  const closureDates = getClosureSet(closures);
  let totalMinutes = 0;

  // Parcourir chaque jour
  const current = new Date(start);
  current.setHours(0, 0, 0, 0);

  while (current <= end) {
    const dateStr = current.toLocaleDateString('en-CA');
    const dayName = DAY_KEYS[current.getDay()];
    const config = businessHours[dayName];

    if (config?.active && !closureDates.has(dateStr)) {
      const [oh, om] = (config.open || '08:00').split(':').map(Number);
      const [ch, cm] = (config.close || '17:30').split(':').map(Number);

      const workStart = new Date(current); workStart.setHours(oh, om, 0, 0);
      const workEnd = new Date(current); workEnd.setHours(ch, cm, 0, 0);

      // Intersection entre [workStart, workEnd] et [start, end]
      const actualStart = start > workStart ? start : workStart;
      const actualEnd = end < workEnd ? end : workEnd;

      if (actualEnd > actualStart) {
        totalMinutes += (actualEnd - actualStart) / 60000;
      }
    }
    current.setDate(current.getDate() + 1);
  }

  return Math.round((totalMinutes / 60) * 10) / 10;
}

/**
 * Calcule la date de fin en fonction d'un volume d'heures
 */
const formatDate = (date) => {
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export function addBusinessHours(startDate, hours, businessHours, closures) {
  if (!startDate || hours <= 0 || !businessHours) return startDate;
  
  const start = new Date(startDate);
  const closureDates = getClosureSet(closures);
  let remainingMinutes = Math.round(hours * 60);
  const current = new Date(start);

  let safety = 0;
  while (remainingMinutes > 0 && safety < 1000) {
    safety++;
    const dateStr = formatDate(current);
    const dayName = DAY_KEYS[current.getDay()];
    const config = businessHours[dayName];

    if (config?.active && !closureDates.has(dateStr)) {
      const [oh, om] = (config.open || '08:00').split(':').map(Number);
      const [ch, cm] = (config.close || '17:30').split(':').map(Number);

      const workStart = new Date(current); workStart.setHours(oh, om, 0, 0);
      const workEnd = new Date(current); workEnd.setHours(ch, cm, 0, 0);

      // Effect Start within this day
      const actualStart = current > workStart ? (current < workEnd ? current : workEnd) : workStart;

      if (actualStart < workEnd) {
        const availableMinutes = Math.round((workEnd - actualStart) / 60000);
        if (remainingMinutes <= availableMinutes) {
          // Finished today!
          const result = new Date(actualStart.getTime() + remainingMinutes * 60000);
          return result.getFullYear() + "-" + 
                 String(result.getMonth() + 1).padStart(2, '0') + "-" + 
                 String(result.getDate()).padStart(2, '0') + "T" + 
                 String(result.getHours()).padStart(2, '0') + ":" + 
                 String(result.getMinutes()).padStart(2, '0');
        } else {
          remainingMinutes -= availableMinutes;
          current.setTime(workEnd.getTime());
        }
      }
    }
    
    // Jump to next day 00:00
    current.setDate(current.getDate() + 1);
    current.setHours(0, 0, 0, 0);
  }
  return current.toISOString();
}

/**
 * Get continuous timeline segments (active and inactive) for a duration
 */
export function getTimelineSegments(startDate, endDate, businessHours, closures) {
  if (!startDate || !endDate) return [];
  const start = new Date(startDate);
  const end = new Date(endDate);
  const closureDates = getClosureSet(closures);
  const segments = [];

  let current = new Date(start);
  while (current < end) {
    const dateStr = current.toLocaleDateString('en-CA');
    const dayName = DAY_KEYS[current.getDay()];
    const config = businessHours?.[dayName] || { active: true, open: '08:00', close: '17:30' };
    const isClosed = config.active === false || closureDates.has(dateStr);

    if (isClosed) {
      const dayEnd = new Date(current);
      dayEnd.setHours(23, 59, 59, 999);
      const finalEnd = dayEnd < end ? dayEnd : end;
      segments.push({ start: current.toISOString(), end: finalEnd.toISOString(), active: false });
      current = new Date(finalEnd.getTime() + 1);
    } else {
      const [oh, om] = (config.open || '08:00').split(':').map(Number);
      const [ch, cm] = (config.close || '17:30').split(':').map(Number);
      
      const workStart = new Date(current); workStart.setHours(oh, om, 0, 0);
      const workEnd = new Date(current); workEnd.setHours(ch, cm, 0, 0);
      const dayEnd = new Date(current); dayEnd.setHours(23, 59, 59, 999);

      // 1. Before Work (Inactive)
      if (current < workStart) {
        const segEnd = workStart < end ? workStart : end;
        segments.push({ start: current.toISOString(), end: segEnd.toISOString(), active: false });
        current = new Date(segEnd);
      }
      if (current >= end) break;

      // 2. During Work (Active)
      if (current >= workStart && current < workEnd) {
        const segEnd = workEnd < end ? workEnd : end;
        segments.push({ start: current.toISOString(), end: segEnd.toISOString(), active: true });
        current = new Date(segEnd);
      }
      if (current >= end) break;

      // 3. After Work (Inactive)
      if (current >= workEnd && current <= dayEnd) {
        const segEnd = dayEnd < end ? dayEnd : end;
        segments.push({ start: current.toISOString(), end: segEnd.toISOString(), active: false });
        current = new Date(segEnd.getTime() + 1); // Move to next day
      }
    }
  }
  return segments;
}

/**
 * Calcul des temps de trajet via OSRM
 */
export async function fetchTravelInfo(lat1, lon1, lat2, lon2) {
  if (!lat1 || !lon1 || !lat2 || !lon2) return null;
  try {
    const url = `https://router.project-osrm.org/route/v1/driving/${lon1},${lat1};${lon2},${lat2}?overview=false`;
    const res = await fetch(url);
    const data = await res.json();
    if (data.routes && data.routes.length > 0) {
      const route = data.routes[0];
      return {
        duration: Math.round(route.duration / 60), // minutes
        distance: Math.round((route.distance / 1000) * 10) / 10 // km
      };
    }
  } catch (err) { console.error("OSRM Error:", err); }
  return null;
}
